import { useState, useEffect } from 'react';

/**
 * 内页「顶部/侧栏锚点滚动导航」的通用交互逻辑（资料片各内页共用）：
 * - activeAnchor：scrollspy 当前命中的锚点（遍历锚点取最后一个越过阈值者）
 * - showSidebar：滚过一段距离后再显示固定侧栏，避免顶部主视觉处遮挡
 * - scrollToAnchor(id)：平滑滚动到 CMS HTML 内对应写死的 id 小节
 * - scrollToTop()：回到页面顶部
 *
 * @param {string[]} anchors - CMS 正文内写死的小节 id 列表（顺序与导航项一致）
 * @param {object} [options]
 * @param {number} [options.anchorOffset=50] - 锚点滚动时的顶部留白
 * @param {number} [options.spyThreshold=200] - scrollspy 命中判定阈值（元素 top <= 该值即命中）
 * @param {number} [options.sidebarShowAt=600] - 滚过多少 px 后显示侧栏
 * @returns {{ activeAnchor: string, showSidebar: boolean, scrollToAnchor: Function, scrollToTop: Function }}
 */
const useAnchorScrollSpy = (anchors, options = {}) => {
  const { anchorOffset = 50, spyThreshold = 200, sidebarShowAt = 600 } = options;
  const [activeAnchor, setActiveAnchor] = useState('');
  const [showSidebar, setShowSidebar] = useState(false);

  const scrollToAnchor = anchorId => {
    const element = document.getElementById(anchorId);
    if (element) {
      const elementTop = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: elementTop - anchorOffset, behavior: 'smooth' });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const handleScroll = () => {
      let currentActive = '';
      for (const id of anchors) {
        const element = document.getElementById(id);
        if (element && element.getBoundingClientRect().top <= spyThreshold) {
          currentActive = id;
        }
      }
      setActiveAnchor(currentActive);
      setShowSidebar(window.pageYOffset >= sidebarShowAt);
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return { activeAnchor, showSidebar, scrollToAnchor, scrollToTop };
};

export default useAnchorScrollSpy;
